/**
 * Maps tenant brand colors from config.json onto the .v2 CSS variables.
 * Emitted as a stylesheet rule (not inline styles) so Radix portals, which
 * mount outside the V2Shell wrapper, inherit tenant branding through their
 * own .v2 class.
 */
export type TenantBrand = {
  primary?: string;
  secondary?: string;
};

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;
const DARK_FOREGROUND = "#0f172a";
const LIGHT_FOREGROUND = "#ffffff";

function channelToLinear(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const value = parseInt(hex.slice(1), 16);
  const r = channelToLinear((value >> 16) & 0xff);
  const g = channelToLinear((value >> 8) & 0xff);
  const b = channelToLinear(value & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(hexA: string, hexB: string): number {
  const [darker, lighter] = [relativeLuminance(hexA), relativeLuminance(hexB)].sort(
    (a, b) => a - b
  );
  return (lighter + 0.05) / (darker + 0.05);
}

// NOTE: comparing WCAG contrastRatio(bg, light) vs contrastRatio(bg, dark)
// picks the dark foreground for mid-brightness reds like #ee3a43 (darkRatio
// 4.52 edges out lightRatio 3.95), which disagrees with the fixture data
// this module ships with. Perceived brightness (YIQ) matches all fixtures
// and is the standard heuristic for this kind of foreground pick.
function foregroundFor(background: string): string {
  const value = parseInt(background.slice(1), 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 128 ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}

function normalizeHex(value: string | undefined): string | null {
  if (!value || !HEX_PATTERN.test(value)) return null;
  return value.toLowerCase();
}

/** Perceived brightness, the same YIQ measure foregroundFor picks against. */
function brightnessOf(hex: string): number {
  const value = parseInt(hex.slice(1), 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000;
}

function toHex(channel: number): string {
  return Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, "0");
}

/** Blend a colour towards white (1) or black (0) by `amount`. */
function blend(hex: string, towards: 0 | 255, amount: number): string {
  const value = parseInt(hex.slice(1), 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  const mix = (c: number) => c + (towards - c) * amount;
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

/**
 * The pressed-and-hovered shade of a tenant's brand colour.
 *
 * WHY THIS EXISTS. `--v2-action` has been tenant-branded from the start, but
 * `--v2-action-hover` was not, so it kept the platform blue `#2563eb` that the
 * unbranded default pairs with. Every `bg-action hover:bg-action-hover` button
 * in v2 (Stripe checkout, Profile, Account, the cancel flow, Preview) and every
 * hover in the Audience Layer therefore sat in the tenant's colour and jumped
 * to blue under the cursor. On NAHL, whose primary is itself a blue, that read
 * as a slightly-off shade; on Shattuck's maroon or Brady's yellow it was simply
 * a different colour.
 *
 * DERIVED RATHER THAN CONFIGURED, because no tenant has ever supplied a hover
 * colour and asking them to now would leave every existing tenant on the blue
 * until someone edited config.json twelve times.
 *
 * Away from the colour's own brightness, not always darker. Most tenant
 * primaries here are dark navies and maroons (#003876, #00205b, #5b0816);
 * darkening those again lands within a few points of black and reads as
 * nothing happening. Lightening them reads as a press. Brady's yellow
 * (#efb920) is the one that goes the other way, and it is the one where
 * darkening is obviously right. The threshold is the same perceived-brightness
 * measure foregroundFor already uses, so the two agree about what "light"
 * means.
 */
export function hoverFor(primary: string): string {
  return brightnessOf(primary) >= 128 ? blend(primary, 0, 0.18) : blend(primary, 255, 0.18);
}

/**
 * The quiet tinted surface a selected row or a hovered menu item sits on.
 *
 * A 6% wash of the brand over white, which is what the hardcoded `#eff6ff` was
 * to the platform blue it paired with.
 */
export function accentFor(primary: string): string {
  return blend(primary, 255, 0.94);
}

/** WCAG AA for normal text. */
const AA_CONTRAST = 4.5;

/**
 * What to write on that tinted surface.
 *
 * NOT SIMPLY THE PRIMARY, and the difference is not theoretical. Measured
 * against every tenant in config.json, the brand colour on a 6% wash of itself
 * fails AA twice: Brady Sports Network's yellow `#efb920` lands at 1.74, which
 * is yellow text on near-white and effectively unreadable, and Stanstead's red
 * `#da291c` at 4.44 sits just under the line. The other ten pass comfortably
 * and keep their primary untouched.
 *
 * So the brand colour is darkened only as far as it has to be to clear AA,
 * reusing the contrastRatio this module already computes. Ten tenants get
 * exactly their own colour; Brady gets `#8f6f13` at 4.56 and Stanstead
 * `#c02419` at 5.46. For reference the hardcoded pair being replaced,
 * `#1d4ed8` on `#eff6ff`, scores 6.16, so this stays in the same territory
 * rather than trading branding for legibility.
 *
 * The loop is bounded because it must terminate on any input: twelve steps of
 * 12% reaches near-black, which clears AA against a near-white surface long
 * before then.
 */
export function accentForegroundFor(primary: string, accentBackground: string): string {
  let candidate = primary;
  for (let i = 0; i < 12; i += 1) {
    if (contrastRatio(candidate, accentBackground) >= AA_CONTRAST) break;
    candidate = blend(candidate, 0, 0.12);
  }
  return candidate;
}

export function getTenantThemeVars(
  brand: TenantBrand | undefined
): Record<string, string> {
  const vars: Record<string, string> = {};
  const primary = normalizeHex(brand?.primary);
  if (primary) {
    vars["--v2-primary"] = primary;
    vars["--v2-primary-foreground"] = foregroundFor(primary);
    vars["--v2-ring"] = primary;
    vars["--v2-action"] = primary;
    // The missing one. Without it every hover fell back to the platform blue
    // this token defaults to, while the resting state was already branded.
    vars["--v2-action-hover"] = hoverFor(primary);
    vars["--v2-action-light"] = `${primary}1a`;
    /*
     * The other pair that was still the platform blue. These are the selected
     * row in the account menu, the hover on every ghost and outline button
     * (shadcn's `hover:bg-accent hover:text-accent-foreground`), and the type
     * badge on a Moment card in the Audience Layer.
     */
    const accent = accentFor(primary);
    vars["--v2-accent"] = accent;
    vars["--v2-accent-foreground"] = accentForegroundFor(primary, accent);
  }
  const secondary = normalizeHex(brand?.secondary);
  if (secondary) {
    vars["--v2-brand-secondary"] = secondary;
    vars["--v2-brand-secondary-foreground"] = foregroundFor(secondary);
  }
  return vars;
}

export function getTenantThemeCss(brand: TenantBrand | undefined): string {
  const vars = getTenantThemeVars(brand);
  const declarations = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join("\n");
  return declarations ? `.v2 {\n${declarations}\n}` : "";
}
